/**
 * Expandable Product Details Component
 * Shows full product information with Add to Cart functionality
 */

'use client';

import { useState } from 'react';
import { X, ShoppingCart, Check, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { trackEvent } from './analytics';

interface ProductDetailsExpandedProps {
  productName: string;
  price: string;
  imageUrl: string;
  reasoning: string;
  description?: string;
  features: string[];
  url: string;
  onClose: () => void;
}

export function ProductDetailsExpanded({
  productName,
  price,
  imageUrl,
  reasoning,
  description,
  features,
  url,
  onClose,
}: ProductDetailsExpandedProps) {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  const handleAddToCart = () => {
    // Track analytics
    trackEvent('add_to_cart_clicked', {
      product: productName,
      price,
      source: 'dreambot_chat',
    });

    // Open product page in new tab (WooCommerce handles cart)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const displayedFeatures = showAllFeatures ? features : features.slice(0, 4);

  return (
    <div className="absolute inset-0 z-50 bg-card flex flex-col animate-message-slide-up">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border/50 shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-[17px] leading-[1.2] tracking-[-0.02em] font-semibold text-foreground break-words">
              {productName}
            </h3>
            <p className="text-[15px] font-semibold text-foreground tabular-nums mt-1">
              {price}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
            aria-label="Close details"
          >
            <X size={16} className="text-foreground" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Product Image */}
          <div className="relative h-48 w-full overflow-hidden bg-muted rounded-xl mb-4">
            <img
              src={imageUrl}
              alt={productName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          {/* Product Description */}
          {description && (
            <div className="mb-4">
              <h4 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                Product Description
              </h4>
              <p className="text-[14px] text-foreground leading-[1.6] tracking-[-0.01em]">
                {description}
              </p>
            </div>
          )}

          {/* Why We Recommend */}
          <div className="mb-4">
            <h4 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Why We Recommend
            </h4>
            <p className="text-[14px] text-foreground leading-[1.6] tracking-[-0.01em]">
              {reasoning}
            </p>
          </div>

          {/* Key Features */}
          <div className="mb-4">
            <h4 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Key Features
            </h4>
            <div className="space-y-2">
              {displayedFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Check size={16} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" strokeWidth={3} />
                  <span className="text-[14px] text-foreground leading-[1.5]">{feature}</span>
                </div>
              ))}
            </div>

            {features.length > 4 && (
              <button
                onClick={() => setShowAllFeatures(!showAllFeatures)}
                className="flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                {showAllFeatures ? (
                  <>
                    <ChevronUp size={14} />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} />
                    Show {features.length - 4} more features
                  </>
                )}
              </button>
            )}
          </div>

          {/* Additional Info */}
          <div className="bg-muted rounded-xl p-3 mb-4">
            <div className="space-y-2 text-[13px] text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check size={14} className="text-green-600 dark:text-green-400" strokeWidth={3} />
                <span>Free shipping on all orders</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3 border-t border-border/50 shrink-0">
          <button
            onClick={handleAddToCart}
            disabled={addedToCart}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-[14px] tracking-[-0.01em] transition-all ${
              addedToCart
                ? 'bg-green-600 text-white'
                : 'bg-foreground text-background hover:opacity-90 active:scale-[0.98]'
            }`}
          >
            {addedToCart ? (
              <>
                <Check size={16} strokeWidth={3} />
                Opening product page
              </>
            ) : (
              <>
                <ShoppingCart size={16} />
                View on Store
              </>
            )}
          </button>
        </div>
    </div>
  );
}
